/**
 * Faunamaster Etablerings- og Såvejledninger
 *
 * Dette modul indeholder officielle etablerings-, maskin- og såvejledninger
 * for samtlige Faunamaster-frøblandinger.
 * Anvendes af Plotwise i rådgivermodulet, frøberegneren og plejeplaner.
 */

export interface PlantingGuide {
  productId: string;
  productName: string;
  aliases: readonly string[];
  producer: 'Faunamaster ApS';
  unit: 'kerner' | 'kg';
  seedingRate: string;
  packSize: string;
  soilTempMinC: number;
  sowingPeriod: string;
  sowingDepthCm: string;
  sowingDepthMm: string;
  seedBed: string;
  rolling: string;
  fertilizer: string;
  weedControl: string;
  seederSetting: string;
  highlights: readonly string[];
  disclaimer: string;
  source: string;
}

const OFFICIAL_DISCLAIMER =
  'Såvejledningen er vejledende og udarbejdet på baggrund af officielle faglige anbefalinger fra Faunamaster ApS. Plotwise, Emil Beiner og tilknyttede rådgivere fralægger sig ethvert erstatningsansvar for manglende spiring, ukrudtstryk, tørkeskader, fugleangreb, vejrlig eller forkert maskinindstilling på det enkelte revir.';

const OFFICIAL_SOURCE =
  'Faunamaster ApS – Officielle produktspecifikationer, såvejledninger og maskinmanualer for Faunamaster Seeder 105 / 180.';

export const FAUNAMASTER_PLANTING_GUIDES: readonly PlantingGuide[] = [
  {
    productId: 'grass',
    productName: 'Hunters Corn',
    aliases: [
      'hunterscorn',
      'hunters corn',
      'vildtmajs',
      'majs',
      'faunamaster-hunterscorn-25-000-korn',
      'fmshc250',
      'faunamaster hunters corn',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kerner',
    seedingRate: '100.000 kerner/ha (4 poser á 25.000 kerner pr. ha)',
    packSize: '25.000 kerner/pose',
    soilTempMinC: 10,
    sowingPeriod: 'Maj – Juni (når jordtemperaturen stabilt er 8–10 °C)',
    sowingDepthCm: '3 – 5 cm',
    sowingDepthMm: '30 – 50 mm',
    seedBed:
      'Dybgrundet, veldrænet og jævnt såbed. Pløjning efterfulgt af grundig harvning til en løs, fugtbevarende såseng uden store knolde eller dybe spor.',
    rolling:
      'Tromling efter såning anbefales kraftigt for at sikre fast kapillær jordkontakt omkring kernen, bevare dybdefugten og reducere fugles adgang.',
    fertilizer:
      'Majs er en næringskrævende afgrøde. Tilfør ca. 80–120 kg N/ha, gerne som NPK-fuldgødning placeret ved såning eller nedharvet umiddelbart før såning.',
    weedControl:
      'Ukrudtsbekæmpelse i de første 4–6 uger er afgørende for succes. Etableres i ukrudtsfri jord; anvend falsk såbed og mekanisk strigling/radrensning før rækkerne lukker.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Monter grov/majs-såvalse (majssæt). Dybdebolt indstilles i hul 4–5 for 30–50 mm sådybde (3–5 cm). Udmader kalibreres til 100.000 kerner/ha.',
    highlights: [
      'Udsædsmængde er udelukkende angivet i kerner (100.000 kerner/ha fordelt på 25.000 kerner/pose).',
      'Kræver lun jord (min. 8–10 °C jordtemperatur) for hurtig spiring; så aldrig i for kold eller vandmættet forårsjord.',
      'Dyb såning (3–5 cm / 30–50 mm) beskytter kernerne mod fugle og sikrer kontakt med fugtig jord.',
      'Tromling efter såning sikrer kapillærfugtighed og god jordkontakt.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'flower',
    productName: 'Boost Blend',
    aliases: [
      'boost blend',
      'boostblend',
      'boost',
      'faunamaster-boost-blend-5-kg',
      'fmsbos5kg',
      'blomsterblanding',
      'vinterboost',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate:
      '6,5 – 10 kg/ha (standard 6,5 kg/ha ved maskinsåning, op til 10 kg/ha ved bredsåning)',
    packSize: '5 kg/pakke (5 kg pose)',
    soilTempMinC: 8,
    sowingPeriod: 'Juni – August',
    sowingDepthCm: '0,5 – 1 cm',
    sowingDepthMm: '5 – 10 mm',
    seedBed:
      'Meget fint smuldret, jævnt og fast såbed. Jorden harves let i overfladen for at bekæmpe ukrudtskim forud for såning.',
    rolling:
      'Let tromling før og efter såning med gitter- eller glattromle for optimal frøkontakt og kapillærvirkning.',
    fertilizer:
      'Moderat gødskning på ca. 40–60 kg N/ha (NPK) ved etablering fremmer hurtig bladvækst og rodudvikling samt sukkerdannelse i roerne.',
    weedControl:
      'Hurtig spiring og hurtig bladdækning kvæler sent ukrudt. Falsk såbed 1–2 uger før såning anbefales til at fjerne frøukrudt.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Finfrøvalse. Dybdebolt i øverste position (hul 1–2) til præcis overfladesåning på 5–10 mm (0,5–1 cm). Bagtromle indstilles til let pakning.',
    highlights: [
      'Dosering: 6,5–10 kg/ha (standard 6,5 kg/ha med Faunamaster Seeder, op til 10 kg/ha ved bredsåning) i 5 kg poser.',
      'Meget små frø: Skal sås overfladisk (0,5–1 cm / 5–10 mm) – for dyb såning medfører manglende fremspiring.',
      'Let tromling før og efter såning sikrer kapillærvirkning og perfekt jordkontakt.',
      'Finfrøvalse på Faunamaster Seeder 105 / 180 sikrer præcis udmadning af de fine frø.',
      'Kål- og roetyper danner sukkerstoffer efter den første nattefrost, hvilket gør vildtageren til en uimodståelig vintermagnet.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'deer',
    productName: 'Deer Blend',
    aliases: [
      'deer blend',
      'deerblend',
      'deer',
      'faunamaster-deer-blend-5-kg',
      'fmsdb5kg',
      'råvildt',
      'hjortevildt',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '12 kg/ha',
    packSize: '5 kg/pakke',
    soilTempMinC: 10,
    sowingPeriod: 'Maj – August (jordtemperatur min. 10 °C)',
    sowingDepthCm: '1 – 1,5 cm',
    sowingDepthMm: '10 – 15 mm',
    seedBed:
      'Velpakket, gennemarbejdet såbed uden dybe spor. Pløjning eller dybdeharvning efterfulgt af en fin og jævn såbedsharvning.',
    rolling:
      'Tromling før såning anbefales for at skabe et fast underlag, efterfulgt af let tromling efter såning for at sikre frøkontakt for både store og små frø.',
    fertilizer:
      'Tilfør 50–70 kg N/ha i etableringsåret. Da blandingen indeholder kvælstoffikserende bælgplanter (lucerne, kløver), reduceres N-behovet markant fra år 2.',
    weedControl:
      'Etablering i en ren jordbund er afgørende. Cikorie og lucerne tåler ikke kraftig ukrudtskonkurrence i kimbladstadiet.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Universal/mellemvalse. Dybdebolt i hul 2–3 (10–15 mm sådybde). Ryste- og doseringsplade indstilles så frøstørrelserne ikke adskiller sig.',
    highlights: [
      'Flerårig magnet til klovbærende vildt: Giver grønfoder i etableringsåret og forstærkes fra år 2 med dybderodende urter og lucerne.',
      'Sådybde ca. 1–1,5 cm ved min. 10 °C jordtemperatur for sikker spiring af cikorie, lucerne og kløver.',
      'Tromling før og efter såning sikrer hurtig og ensartet etablering.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'clover',
    productName: 'Deer Explosion',
    aliases: [
      'deer explosion',
      'deerexplosion',
      'clover',
      'faunamaster-deer-explosion-5-kg',
      'fmsdeex5k',
      'kløver græs',
      'kloever graes',
      'kløvergræs',
      'kloevergraes',
      'kløver',
      'klover',
      'proteinblanding',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '9 kg/ha',
    packSize: '5 kg/pakke',
    soilTempMinC: 8,
    sowingPeriod: 'April – August',
    sowingDepthCm: '0,5 – 1 cm',
    sowingDepthMm: '5 – 10 mm',
    seedBed:
      'Ekstremt fast, jævnt og fint såbed. Kløver- og urtefrø kræver millimetersåning uden knolde eller løse luftlommer i jorden.',
    rolling:
      'Obligatorisk tromling før såning (jorden skal bære en person uden dybe fodspor) og tromling efter såning for optimal kapillær fugtighed.',
    fertilizer:
      'Lavt kvælstofbehov (20–40 kg N/ha ved såning). Blandingen indeholder hvid-, rød-, blod- og balansakløver samt lucerne, som selv fikserer kvælstof. Tilfør gerne PK-gødning og kalk ved lavt reaktionstal (pH bør være > 6,5).',
    weedControl:
      'Renholdelse inden såning er kritisk, da kløverarter etablerer sig langsomt i de første uger. Efter etablering danner blandingen et tæt, flerårigt tæppe.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Finfrøvalse. Dybdebolt i hul 1 (meget lav dybde: 5–10 mm). Bagrulle indstilles til direkte kontakt med overfladen.',
    highlights: [
      '3–5 årig protein- og mineralbombe til råvildt og dåvildt, der booster opsatsudvikling og kropsvægt.',
      'Meget overfladisk såning (0,5–1 cm) i et fast, grundigt tromlet såbed.',
      'Fikserer sit eget kvælstof via symbiotiske rodknoldbakterier.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'pheasant',
    productName: 'Fasan Blanding',
    aliases: [
      'fasan blanding',
      'fasanblanding',
      'fasan',
      'pheasant',
      'faunamaster-fasan-blanding-5kg',
      'fmspb5kg',
      'fuglevildt',
      'agerhøns',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '25 kg/ha',
    packSize: '5 kg/pakke',
    soilTempMinC: 10,
    sowingPeriod: 'Maj – Juni',
    sowingDepthCm: '1 – 2 cm',
    sowingDepthMm: '10 – 20 mm',
    seedBed:
      'Standard pløjet og harvet såbed med god krummestruktur. Undgå for fin smuldring på lerjord for at forhindre jordskorpedannelse efter slagregn.',
    rolling: 'Tromling efter såning for at lukke såsporene og give frøene god jordkontakt.',
    fertilizer:
      '60–80 kg N/ha i fuldgødning (NPK). Solsikke, majs og hirse kvitterer markant for god næringsstoftilgængelighed i forsommeren.',
    weedControl:
      'Mekanisk ukrudtsharvning før fremspiring kan foretages. Blandingen vokser hurtigt til og giver god bundskygge mod sommerukrudt.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Universal/blandingsvalse. Dybdebolt i hul 2–3 (10–20 mm sådybde). Rystearm indstilles så større frø (solsikke/majs) og mindre frø (hirse/raps) forbliver blandede i frøkassen.',
    highlights: [
      'Optimal balance mellem frøproducerende planter (solsikke, hirse, majs) og dækningsplanter (marvkål, sorthavre).',
      'Sås ved min. 10 °C jordtemperatur i maj-juni for at sikre varmekrævende hirse og majs.',
      'Giver standpladser, rigt insektliv til kyllinger samt vinterføde til fasaner og agerhøns.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'winter',
    productName: 'Flush Blend',
    aliases: [
      'flush blend',
      'flushblend',
      'flush',
      'winter',
      'faunamaster-flush-blend-5-kg',
      'fmsfl5kg',
      'vildtager struktur',
      'vildtagerstruktur',
      'struktur',
      'struktur mix',
      'dækningsblanding',
      'remiseblanding',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '25 kg/ha',
    packSize: '5 kg/pakke',
    soilTempMinC: 10,
    sowingPeriod: 'Maj – Juni',
    sowingDepthCm: '2 – 3 cm',
    sowingDepthMm: '20 – 30 mm',
    seedBed:
      'Dybdeharvet eller pløjet jord med god luft- og vandkapacitet, velegnet til det kraftige rodsystem hos sorghum, majs og hamp.',
    rolling: 'Tromling efter såning for at fastholde jordfugtigheden omkring de større frø.',
    fertilizer:
      '70–100 kg N/ha (NPK). Rigelig næring er en forudsætning for at nå den maksimale plantehøjde (op til 2–2,5 m) og stive stængler.',
    weedControl:
      'Etableres i velrenset jord. Den kraftige tilvækst lukker af for ukrudtet i løbet af 4–5 uger.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Grovvalse. Dybdebolt i hul 3–4 (20–30 mm sådybde). Sikr fri gennemstrømning af de store stængelfrø (majs, hamp, sorghum).',
    highlights: [
      'Skabt til vildtkorridorer, remisedække og drivjagter: Bevarer strukturen og holder vildtet dækket selv under kraftigt snefald.',
      'Højtvoksende plantetyper (vildtmajs, sorghum, hamp) danner faste standpladser og flugtveje.',
      'Kræver lun jord (min. 10 °C) og god gødskning for at opnå fuld højde og stængelstyrke.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'cover',
    productName: 'Fall Rapid',
    aliases: [
      'fall rapid',
      'fallrapid',
      'rapid',
      'cover',
      'nyhed-faunamaster-fall-rapid-5-kg',
      'fmsfrd5kg',
      'efterårs mix',
      'efteraars mix',
      'efterårsblanding',
      'efteraarsblanding',
      'efterår',
      'efteraar',
      'reparationsblanding',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '20 kg/ha',
    packSize: '5 kg/pakke',
    soilTempMinC: 6,
    sowingPeriod: 'Juli – September',
    sowingDepthCm: '1 – 2 cm',
    sowingDepthMm: '10 – 20 mm',
    seedBed:
      'Hurtig harvning eller stubharvning er ofte tilstrækkeligt efter forudgående afgrøde. Kan også etableres i traditionelt pløjet såbed.',
    rolling: 'Tromling umiddelbart efter såning sikrer hurtig fugtoptagelse i sensommeren.',
    fertilizer:
      '30–50 kg N/ha ved såning booster den eksplosive grønne vækst inden den første nattefrost.',
    weedControl:
      'Ekstremt hurtig spiring og hurtigt bunddække kvæler fremspirende ukrudt effektivt uden behov for kemi.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Universalvalse. Dybdebolt i hul 2 (10–20 mm sådybde). Særdeles velegnet til direkte såning i let bearbejdet stub.',
    highlights: [
      'Ekspres-etablering: Hurtigtvoksende grønt tæppe til sensommer- og efterårssåning.',
      'Perfekt som reparationsblanding ved mislykkede forårsarealer eller efter tidlig kornhøst.',
      'Kombinerer kulderesistente kålarter, vintervikke og rug til foder og dække langt ind i vinteren.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
  {
    productId: 'brassica',
    productName: 'Bestøverbrak uden græs',
    aliases: [
      'bestøverbrak uden græs',
      'bestøverbrak',
      'bestoverbrak uden graes',
      'bestoverbrak',
      'brassica',
      'bestoverbrak-uden-graes-2-arig-10-kg',
      'fmsbb10k',
      'blomsterbrak',
      'brak',
      'bestøver',
    ],
    producer: 'Faunamaster ApS',
    unit: 'kg',
    seedingRate: '8 – 10 kg/ha',
    packSize: '10 kg/pakke',
    soilTempMinC: 8,
    sowingPeriod: 'April – Juni',
    sowingDepthCm: '0,5 – 1 cm',
    sowingDepthMm: '5 – 10 mm',
    seedBed:
      'Fint, jævnt og fast såbed. Jorden skal være renset for flerårigt rodukrudt som kvik og tidsler før etablering.',
    rolling: 'Tromling før og efter såning er afgørende for de fine urte- og blomsterfrø.',
    fertilizer:
      'Minimer eller undlad kvælstofgødskning (0–30 kg N/ha). Høj gødskning fremmer ukrudtsgræsser og kvæler de sarte blomsterplanter.',
    weedControl:
      'Mekanisk renholdelse før såning via falsk såbed. Efter såning må der ikke strigles, da blomsterurterne spirer forskudt over flere uger.',
    seederSetting:
      'Faunamaster Seeder 105 / 180: Finfrøvalse. Dybdebolt i hul 1 (5–10 mm sådybde). Udmades jævnt ved lav kørehastighed.',
    highlights: [
      'Godkendt 2-årig blomsterrig blanding til bestøverbrak helt uden græsser.',
      'Skal sås meget overfladisk (0,5–1 cm) i et fast, tromlet såbed.',
      'Skaber et rigt insekt- og sommerfugleliv, der er livsvigtigt for fasankyllingers overlevelse.',
    ],
    disclaimer: OFFICIAL_DISCLAIMER,
    source: OFFICIAL_SOURCE,
  },
];

/**
 * Normaliserer søgestrenge til sammenligning (fjerner accenter, mellemrum, bindestreger og gør lowercase).
 */
function normalizeKey(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // fjerner diakritiske tegn hvis relevant
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Slår en officiel Faunamaster såvejledning op baseret på productId, produktnavn eller synonym/alias.
 */
export function getPlantingGuide(productIdOrName: string): PlantingGuide | undefined {
  if (!productIdOrName || typeof productIdOrName !== 'string') {
    return undefined;
  }

  const raw = productIdOrName.trim();
  const normalized = normalizeKey(raw);

  if (!normalized) {
    return undefined;
  }

  // 1. Eksakt match på productId, produktnavn eller synonymer/aliases
  const exactMatch = FAUNAMASTER_PLANTING_GUIDES.find((guide) => {
    if (normalizeKey(guide.productId) === normalized) return true;
    if (normalizeKey(guide.productName) === normalized) return true;
    return guide.aliases.some((alias) => normalizeKey(alias) === normalized);
  });
  if (exactMatch) return exactMatch;

  // 2. Delvis/fuzzy match hvis navnet indeholder et produktnavn eller alias (min. 4 tegn)
  return FAUNAMASTER_PLANTING_GUIDES.find((guide) => {
    const prodNameKey = normalizeKey(guide.productName);
    if (
      prodNameKey.length >= 4 &&
      (normalized.includes(prodNameKey) || prodNameKey.includes(normalized))
    ) {
      return true;
    }
    return guide.aliases.some((alias) => {
      const aliasKey = normalizeKey(alias);
      return (
        aliasKey.length >= 5 && (normalized.includes(aliasKey) || aliasKey.includes(normalized))
      );
    });
  });
}

/**
 * Returnerer samtlige officielle Faunamaster såvejledninger som array.
 */
export function getAllPlantingGuides(): PlantingGuide[] {
  return [...FAUNAMASTER_PLANTING_GUIDES];
}
